'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Solicitud } from '@/types/solicitud';
import { solicitudService } from '@/lib/api/solicitudService';
import { expedientesService } from '@/lib/api/expedientesService';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/lib/api/userService';
import { User } from '@/types/user';
// Normalizar texto para búsqueda (quitar acentos)
const normalizeText = (text: string): string => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const matchIncludes = (value: string | undefined, term: string) => {
  if (!term) return true;
  return normalizeText(value || '').includes(normalizeText(term));
};

const isWithinRange = (value?: string, start?: string, end?: string) => {
  if (!value) return false;
  const v = new Date(value);
  if (Number.isNaN(v.getTime())) return false;
  if (start) {
    const s = new Date(start);
    if (!Number.isNaN(s.getTime()) && v < s) return false;
  }
  if (end) {
    const e = new Date(end);
    if (!Number.isNaN(e.getTime()) && v > e) return false;
  }
  return true;
};

// Función para calcular el estatus del expediente según las fechas
const calcularEstatusExpediente = (s: Solicitud): string => {
  const fechaRecibido = s.fechaExpedienteRecibidoRiesgos;
  const fechaRevisado = s.fechaExpedienteRevisadoRiesgos;
  const fechaDevuelto = s.fechaExpedienteDevueltoAComercial;
  const fechaBoveda = s.fechaExpedienteResguardadoEnBoveda;

  // Si hay fecha de bóveda y es la más reciente
  if (fechaBoveda) {
    const fechaBovedaTime = new Date(fechaBoveda).getTime();
    if (fechaDevuelto) {
      const fechaDevueltoTime = new Date(fechaDevuelto).getTime();
      if (fechaBovedaTime > fechaDevueltoTime) {
        return 'Boveda';
      }
    }
    return 'Boveda';
  }

  // Si hay fecha de devolución y es la más reciente
  if (fechaDevuelto) {
    const fechaDevueltoTime = new Date(fechaDevuelto).getTime();
    if (fechaBoveda) {
      const fechaBovedaTime = new Date(fechaBoveda).getTime();
      if (fechaDevueltoTime > fechaBovedaTime) {
        return 'Comercial';
      }
    }
    if (fechaRecibido) {
      const fechaRecibidoTime = new Date(fechaRecibido).getTime();
      if (fechaDevueltoTime > fechaRecibidoTime) {
        return 'Comercial';
      }
    }
    if (fechaRevisado) {
      const fechaRevisadoTime = new Date(fechaRevisado).getTime();
      if (fechaDevueltoTime > fechaRevisadoTime) {
        return 'Comercial';
      }
    }
    return 'Comercial';
  }

  // Si hay fecha de revisión
  if (fechaRevisado) {
    return 'Riesgos';
  }

  // Si hay fecha de recepción
  if (fechaRecibido) {
    const fechaRecibidoTime = new Date(fechaRecibido).getTime();
    if (fechaDevuelto) {
      const fechaDevueltoTime = new Date(fechaDevuelto).getTime();
      if (fechaRecibidoTime > fechaDevueltoTime) {
        return 'Riesgos';
      }
    }
    return 'Riesgos';
  }

  // Por defecto
  return 'Comercial';
};

// Filtro inicial: 01/01/2026. Solo aplicar filtro de fechas cuando el usuario lo cambie manualmente.
const FECHA_INICIAL_DEFAULT = "2026-01-01";

const ControlExpedientesManagement: React.FC = () => {
  const { user: authUser, isFieldAllowed } = useAuth();
  const canEditFechaExpedienteRecibidoRiesgos = isFieldAllowed('fechaExpedienteRecibidoRiesgos');
  const canEditFechaExpedienteRevisadoRiesgos = isFieldAllowed('fechaExpedienteRevisadoRiesgos');
  const canEditFechaExpedienteDevueltoAComercial = isFieldAllowed('fechaExpedienteDevueltoAComercial');
  const canEditFechaExpedienteResguardadoEnBoveda = isFieldAllowed('fechaExpedienteResguardadoEnBoveda');
  const canSeeUsuarioExpedienteRecibidoRiesgos = isFieldAllowed('usuarioExpedienteRecibidoRiesgos');
  const canSeeUsuarioExpedienteRevisadoRiesgos = isFieldAllowed('usuarioExpedienteRevisadoRiesgos');
  const canSeeUsuarioExpedienteDevueltoAComercial = isFieldAllowed('usuarioExpedienteDevueltoAComercial');
  const canSeeUsuarioExpedienteResguardadoEnBoveda = isFieldAllowed('usuarioExpedienteResguardadoEnBoveda');
  const canEditComentariosResguardoExpedientes = isFieldAllowed('comentariosResguardoExpedientes');
  const pathname = usePathname();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(FECHA_INICIAL_DEFAULT);
  const [fechaFin, setFechaFin] = useState('');
  const [producto, setProducto] = useState<'General' | 'Nómina' | 'Adelanto'>('General');
  const [region, setRegion] = useState<'General' | 'CDMX' | 'Guadalajara'>('General');
  const [asesor, setAsesor] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [cliente, setCliente] = useState('');
  const [numeroControl, setNumeroControl] = useState('');
  const [estatusResguardo, setEstatusResguardo] = useState<'General' | 'Comercial' | 'Riesgos' | 'Boveda'>('General');

  // Estado local para edición
  const [editingSolicitudes, setEditingSolicitudes] = useState<Record<string, Partial<Solicitud>>>({});
  // Último valor guardado de comentarios por solicitud (para mostrar botón solo cuando hay cambios)
  const [lastSavedComentarios, setLastSavedComentarios] = useState<Record<string, string>>({});

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await expedientesService.getSolicitudes(
        fechaInicio || undefined,
        fechaFin || undefined,
      );
      if (res.data) {
        const solicitudesConEstatus = res.data.map(s => ({
          ...s,
          expedienteResguardoStatus: s.expedienteResguardoStatus || calcularEstatusExpediente(s),
        }));
        setSolicitudes(solicitudesConEstatus);
        setLastSavedComentarios(prev => {
          const next = { ...prev };
          res.data!.forEach(s => { if (s.id) next[s.id] = s.comentariosResguardoExpedientes || ''; });
          return next;
        });
      } else {
        setError(res.error || 'Error al cargar solicitudes');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar solicitudes');
      console.error('Error fetching solicitudes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const authSub = authUser?.sub ?? null;
  useEffect(() => {
    if (!authSub) return;
    let cancelled = false;
    userService.getUser(authSub).then((response) => {
      if (!cancelled && response.data) setCurrentUser(response.data);
    }).catch((err) => {
      if (!cancelled && process.env.NODE_ENV === 'development') console.error('Error cargando usuario:', err);
    });
    return () => { cancelled = true; };
  }, [authSub]);

  useEffect(() => {
    load();
  }, [pathname]);

  const filtered = useMemo(() => {
    let list = solicitudes;

    // Legacy: si hay numeroControl, no se aplican filtros de fecha/producto/region/asesor/empresa/cliente
    if (!numeroControl.trim()) {
      // Filtro por fechas (incluye valor inicial 01/01/2026)
      if (fechaInicio || fechaFin?.trim()) {
        list = list.filter((s) => {
          const fechaEntrega = s.fechaEntregaARiesgos || '';
          const fechaContacto = s.fechaContacto || '';
          const tieneBuro = Boolean(s.tipoBuroCredito);
          // Usar fechaEntrega si existe, si no fechaContacto (cuando tiene tipoBuro)
          const fechaRef = fechaEntrega || (tieneBuro ? fechaContacto : '');
          if (!fechaRef) return false; // Sin fecha relevante, excluir
          // fechaInicio: pasar si fechaRef >= inicio
          if (fechaInicio) {
            if (!isWithinRange(fechaRef, fechaInicio, undefined)) return false;
          }
          // fechaFin: pasar si fechaRef <= fin
          if (fechaFin?.trim()) {
            if (!isWithinRange(fechaRef, undefined, fechaFin)) return false;
          }
          return true;
        });
      }

      // Filtro por producto
      if (producto !== 'General') {
        if (producto === 'Adelanto') {
          list = list.filter((s) => (s.producto || '') === 'Adelanto de Nómina');
        } else {
          list = list.filter((s) => (s.producto || '') === 'Nómina');
        }
      }

      // Filtro por región
      if (region !== 'General') {
        list = list.filter((s) => (s.regionNombre || '') === region);
      }

      // Filtro por asesor
      if (asesor.trim()) {
        list = list.filter((s) => matchIncludes(s.asesorNombre, asesor));
      }

      // Filtro por empresa
      if (empresa.trim()) {
        list = list.filter((s) => matchIncludes(s.clienteEmpresa, empresa));
      }

      // Filtro por cliente
      if (cliente.trim()) {
        list = list.filter((s) => {
          const nombre = `${s.clienteNombre || ''} ${s.clienteApellidoPaterno || ''} ${s.clienteApellidoMaterno || ''}`;
          return matchIncludes(nombre, cliente);
        });
      }
    }

    // Filtro por número de control (siempre aplicado si se proporciona)
    if (numeroControl.trim()) {
      list = list.filter((s) => matchIncludes(s.solicitudNumeroControl, numeroControl));
    }

    // Filtro por estatus de resguardo
    if (estatusResguardo !== 'General') {
      list = list.filter((s) => {
        const estatus = calcularEstatusExpediente(s);
        return estatus === estatusResguardo;
      });
    }

    // Ordenar por fechaContacto ascendente
    list = list.sort((a, b) => {
      const fechaA = a.fechaContacto ? new Date(a.fechaContacto).getTime() : 0;
      const fechaB = b.fechaContacto ? new Date(b.fechaContacto).getTime() : 0;
      return fechaA - fechaB;
    });

    return list;
  }, [solicitudes, fechaInicio, fechaFin, producto, region, asesor, empresa, cliente, numeroControl, estatusResguardo]);

  const formatLegacyDateTime = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const handleToggleFecha = async (solicitud: Solicitud, field: keyof Solicitud, enabled: boolean) => {
    if (!solicitud.id) return;
    
    const fechaValue = enabled ? formatLegacyDateTime() : '';
    const userName = currentUser?.name || (authUser as any)?.name || 'Sistema';
    
    // Actualizar estado local primero
    setEditingSolicitudes(prev => ({
      ...prev,
      [solicitud.id!]: {
        ...prev[solicitud.id!],
        [field]: fechaValue,
      },
    }));

    // Actualizar en backend (fecha + usuario que activó el switch)
    try {
      const response = await solicitudService.updateField(
        solicitud.id,
        String(field),
        fechaValue,
        'Control de Expedientes',
        userName
      );
      
      if (response.data) {
        const r = response.data as unknown as Record<string, unknown>;
        const getVal = (camel: string, snake: string) => r[camel] ?? r[snake];
        // Valores de la respuesta; si faltan, mantener el que acabamos de enviar para el campo editado
        const updated: Solicitud = {
          ...solicitud,
          ...response.data,
          id: String(r.id ?? response.data.id ?? solicitud.id),
          fechaExpedienteRecibidoRiesgos: (getVal('fechaExpedienteRecibidoRiesgos', 'fecha_expediente_recibido_riesgos') ?? (field === 'fechaExpedienteRecibidoRiesgos' ? fechaValue : undefined)) as string | undefined,
          fechaExpedienteRevisadoRiesgos: (getVal('fechaExpedienteRevisadoRiesgos', 'fecha_expediente_revisado_riesgos') ?? (field === 'fechaExpedienteRevisadoRiesgos' ? fechaValue : undefined)) as string | undefined,
          fechaExpedienteDevueltoAComercial: (getVal('fechaExpedienteDevueltoAComercial', 'fecha_expediente_devuelto_a_comercial') ?? (field === 'fechaExpedienteDevueltoAComercial' ? fechaValue : undefined)) as string | undefined,
          fechaExpedienteResguardadoEnBoveda: (getVal('fechaExpedienteResguardadoEnBoveda', 'fecha_expediente_resguardado_en_boveda') ?? (field === 'fechaExpedienteResguardadoEnBoveda' ? fechaValue : undefined)) as string | undefined,
          usuarioExpedienteRecibidoRiesgos: (getVal('usuarioExpedienteRecibidoRiesgos', 'usuario_expediente_recibido_riesgos') ?? (field === 'fechaExpedienteRecibidoRiesgos' && enabled ? userName : undefined)) as string | undefined,
          usuarioExpedienteRevisadoRiesgos: (getVal('usuarioExpedienteRevisadoRiesgos', 'usuario_expediente_revisado_riesgos') ?? (field === 'fechaExpedienteRevisadoRiesgos' && enabled ? userName : undefined)) as string | undefined,
          usuarioExpedienteDevueltoAComercial: (getVal('usuarioExpedienteDevueltoAComercial', 'usuario_expediente_devuelto_a_comercial') ?? (field === 'fechaExpedienteDevueltoAComercial' && enabled ? userName : undefined)) as string | undefined,
          usuarioExpedienteResguardadoEnBoveda: (getVal('usuarioExpedienteResguardadoEnBoveda', 'usuario_expediente_resguardado_en_boveda') ?? (field === 'fechaExpedienteResguardadoEnBoveda' && enabled ? userName : undefined)) as string | undefined,
          expedienteResguardoStatus: (calcularEstatusExpediente(response.data as Solicitud) || (response.data as Solicitud).expedienteResguardoStatus || (r.expedienteResguardoStatus ?? r.expediente_resguardo_status) || '') as string,
        };
        setSolicitudes(prev => prev.map(s => s.id === updated.id ? updated : s));
        setEditingSolicitudes(prev => {
          const newState = { ...prev };
          delete newState[solicitud.id!];
          return newState;
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Error al actualizar ${field}`);
      console.error('Error updating field:', err);
    }
  };

  const handleUpdateComentarios = async (solicitud: Solicitud, comentarios: string) => {
    if (!solicitud.id) return;
    
    try {
      const response = await solicitudService.updateField(
        solicitud.id,
        'comentariosResguardoExpedientes',
        comentarios,
        'Control de Expedientes'
      );
      
      if (response.data) {
        setSolicitudes(prev => prev.map(s => s.id === response.data!.id ? response.data! : s));
        setLastSavedComentarios(prev => ({ ...prev, [solicitud.id!]: comentarios }));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar comentarios');
      console.error('Error updating comentarios:', err);
    }
  };

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-MX', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Control de Expedientes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Rastrea el flujo físico de los expedientes de crédito entre áreas (Comercial, Riesgos, Bóveda)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={isLoading}>
            {isLoading ? "Cargando…" : "Consultar"}
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onRetry={load} />}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={producto}
              onChange={(e) => setProducto(e.target.value as any)}
            >
              <option value="General">General</option>
              <option value="Nómina">Nómina</option>
              <option value="Adelanto">Adelanto</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Región</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={region}
              onChange={(e) => setRegion(e.target.value as any)}
            >
              <option value="General">General</option>
              <option value="CDMX">CDMX</option>
              <option value="Guadalajara">Guadalajara</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asesor</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={asesor}
              onChange={(e) => setAsesor(e.target.value)}
              placeholder="Buscar asesor..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              placeholder="Buscar empresa..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Buscar cliente..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Núm. Control</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={numeroControl}
              onChange={(e) => setNumeroControl(e.target.value)}
              placeholder="Buscar control..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resguardo</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={estatusResguardo}
              onChange={(e) => setEstatusResguardo(e.target.value as any)}
            >
              <option value="General">General</option>
              <option value="Comercial">Comercial</option>
              <option value="Riesgos">Riesgos</option>
              <option value="Boveda">Bóveda</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div>
            Mostrando <span className="font-medium text-gray-700">{filtered.length}</span> expediente(s)
          </div>
        </div>
      </div>

      {/* Tabla de expedientes - distribución como en imagen */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-gray-100 text-gray-700 sticky top-0 z-10">
            <tr>
              <th className="text-center p-2 font-semibold border border-gray-200 w-10">#</th>
              <th className="text-center p-2 font-semibold border border-gray-200 w-28">Asesor</th>
              <th className="text-center p-2 font-semibold border border-gray-200 w-36 max-w-[10rem]">Expediente</th>
              <th className="text-center p-2 font-semibold border border-gray-200 w-24">Resguardado por</th>
              <th className="text-center p-2 font-semibold border border-gray-200 w-24">Contrato Impreso</th>
              <th className="text-center p-2 font-semibold border border-gray-200" colSpan={2}>Entregado a Riesgos</th>
              <th className="text-center p-2 font-semibold border border-gray-200" colSpan={2}>Revisado por Riesgos</th>
              <th className="text-center p-2 font-semibold border border-gray-200" colSpan={2}>Devuelto a Comercial</th>
              <th className="text-center p-2 font-semibold border border-gray-200" colSpan={2}>En Bóveda</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filtered.length === 0 ? (
              <tr>
                <td className="p-6 text-gray-500 text-center border border-gray-200" colSpan={13}>
                  Sin expedientes con los filtros actuales
                </td>
              </tr>
            ) : (
              filtered.map((s, idx) => {
                const estatus = calcularEstatusExpediente(s);
                const editing = editingSolicitudes[s.id || ''];
                const fechaRecibido = editing?.fechaExpedienteRecibidoRiesgos ?? s.fechaExpedienteRecibidoRiesgos;
                const fechaRevisado = editing?.fechaExpedienteRevisadoRiesgos ?? s.fechaExpedienteRevisadoRiesgos;
                const fechaDevuelto = editing?.fechaExpedienteDevueltoAComercial ?? s.fechaExpedienteDevueltoAComercial;
                const fechaBoveda = editing?.fechaExpedienteResguardadoEnBoveda ?? s.fechaExpedienteResguardadoEnBoveda;
                const montoFmt = s.montoAutorizado != null ? `$${Number(s.montoAutorizado).toLocaleString('es-MX')}` : '';

                return (
                  <React.Fragment key={s.id ?? idx}>
                    {/* Fila principal */}
                    <tr className="hover:bg-blue-50/30 transition-colors">
                      <td className="p-2 text-gray-500 font-medium bg-gray-50 border border-gray-200 align-middle text-center" rowSpan={2}>
                        {idx + 1}
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center" rowSpan={2}>
                        {s.asesorNombre || '—'}
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center" rowSpan={2}>
                        <a
                          href={s.documentos || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-1.5 rounded-md bg-gradient-to-br from-slate-50 to-blue-50/40 border border-slate-100 hover:border-blue-200 hover:from-blue-50/60 hover:to-slate-50 transition-all duration-200 group text-center mx-auto max-w-full"
                        >
                          <div className="flex items-baseline gap-1 flex-wrap justify-center">
                            <span className="font-mono text-xs font-semibold text-blue-700 group-hover:text-blue-800 truncate max-w-full">
                              {s.solicitudNumeroControl || '—'}
                            </span>
                            {montoFmt && (
                              <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded shrink-0">
                                {montoFmt}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-[11px] font-medium text-gray-800 tracking-wide truncate" title={[s.clienteNombre, s.clienteApellidoPaterno, s.clienteApellidoMaterno].filter(Boolean).join(' ').toUpperCase()}>
                            {([s.clienteNombre, s.clienteApellidoPaterno, s.clienteApellidoMaterno].filter(Boolean).join(' ') || '-').toUpperCase()}
                          </div>
                          {s.clienteEmpresa && (
                            <div className="mt-0.5 text-[10px] text-slate-500 italic truncate" title={s.clienteEmpresa}>{s.clienteEmpresa}</div>
                          )}
                          <div className="mt-1 flex flex-wrap gap-0.5 justify-center">
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-slate-200/80 text-slate-700 shrink-0">
                              Buro: {s.tipoBuroCredito || 'No Consultado'}
                            </span>
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-700 shrink-0">
                              {s.solicitudEstatus || '—'}
                            </span>
                          </div>
                        </a>
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center" rowSpan={2}>
                        <div className="font-medium text-gray-600 text-xs">{estatus}</div>
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center text-xs text-gray-600" rowSpan={2}>
                        {formatDate(s.fechaContratoImpreso)}
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center">
                        <div className="text-xs">{formatDate(fechaRecibido)}</div>
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center w-12">
                        {canEditFechaExpedienteRecibidoRiesgos ? (
                          <Switch
                            checked={Boolean(fechaRecibido)}
                            onChange={(enabled) => handleToggleFecha(s, 'fechaExpedienteRecibidoRiesgos', enabled)}
                            label=""
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center">
                        <div className="text-xs">{formatDate(fechaRevisado)}</div>
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center w-12">
                        {canEditFechaExpedienteRevisadoRiesgos ? (
                          <Switch
                            checked={Boolean(fechaRevisado)}
                            onChange={(enabled) => handleToggleFecha(s, 'fechaExpedienteRevisadoRiesgos', enabled)}
                            label=""
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center">
                        <div className="text-xs">{formatDate(fechaDevuelto)}</div>
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center w-12">
                        {canEditFechaExpedienteDevueltoAComercial ? (
                          <Switch
                            checked={Boolean(fechaDevuelto)}
                            onChange={(enabled) => handleToggleFecha(s, 'fechaExpedienteDevueltoAComercial', enabled)}
                            label=""
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center">
                        <div className="text-xs">{formatDate(fechaBoveda)}</div>
                      </td>
                      <td className="p-2 border border-gray-200 align-middle text-center w-12">
                        {canEditFechaExpedienteResguardadoEnBoveda ? (
                          <Switch
                            checked={Boolean(fechaBoveda)}
                            onChange={(enabled) => handleToggleFecha(s, 'fechaExpedienteResguardadoEnBoveda', enabled)}
                            label=""
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                    {/* Sub-fila: usuarios */}
                    <tr className="hover:bg-blue-50/30 transition-colors">
                      <td className="p-1 border border-gray-200 align-middle text-center text-xs text-gray-600" colSpan={2}>
                        {canSeeUsuarioExpedienteRecibidoRiesgos ? (s.usuarioExpedienteRecibidoRiesgos || '—') : '—'}
                      </td>
                      <td className="p-1 border border-gray-200 align-middle text-center text-xs text-gray-600" colSpan={2}>
                        {canSeeUsuarioExpedienteRevisadoRiesgos ? (s.usuarioExpedienteRevisadoRiesgos || '—') : '—'}
                      </td>
                      <td className="p-1 border border-gray-200 align-middle text-center text-xs text-gray-600" colSpan={2}>
                        {canSeeUsuarioExpedienteDevueltoAComercial ? (s.usuarioExpedienteDevueltoAComercial || '—') : '—'}
                      </td>
                      <td className="p-1 border border-gray-200 align-middle text-center text-xs text-gray-600" colSpan={2}>
                        {canSeeUsuarioExpedienteResguardadoEnBoveda ? (s.usuarioExpedienteResguardadoEnBoveda || '—') : '—'}
                      </td>
                    </tr>
                    {canEditComentariosResguardoExpedientes && (
                    <tr className="hover:bg-blue-50/30 transition-colors">
                      <td className="p-2 border border-gray-200 text-center text-xs font-medium text-gray-600 align-middle" colSpan={3}>
                        Comentarios:
                      </td>
                      <td className="p-2 border border-gray-200 align-middle" colSpan={10}>
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            className="flex-1 min-w-0 px-2 py-1 text-xs border rounded border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={s.comentariosResguardoExpedientes || ''}
                            onChange={(e) =>
                              setSolicitudes(prev => prev.map(x =>
                                x.id === s.id ? { ...x, comentariosResguardoExpedientes: e.target.value } : x
                              ))
                            }
                            onBlur={(e) => handleUpdateComentarios(s, e.target.value)}
                            placeholder="Comentarios..."
                          />
                          {(s.comentariosResguardoExpedientes || '') !== (lastSavedComentarios[s.id || ''] ?? '') && (
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => handleUpdateComentarios(s, s.comentariosResguardoExpedientes || '')}
                              className="shrink-0 text-xs py-1 px-2"
                            >
                              Guardar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {filtered.length > 0 && (
        <div className="text-right text-xs text-gray-400 px-2">
          Total: {filtered.length} expediente(s)
        </div>
      )}
    </div>
  );
};

export default ControlExpedientesManagement;
